import { replace, type Params } from 'react-router-dom';

// Utils
import { readCreationDraft } from '../../../../utils/draftScopes';

/**
 * Identidad del borrador del asistente de CREACIÓN de actuaciones (spec
 * `2026-11-02-wizard-draft-ownership-design.md` § 6 y § 7, fila «Crear
 * actuación»).
 *
 * Crear no lleva id en la URL y, sin red o en el paso 1, la actuación ni
 * siquiera existe en el servidor: el dueño de las copias de las pestañas y la
 * `key` de la pantalla es un id LOCAL del borrador, `draftId`, que escribe en
 * `new-action-data` cada puerta que empieza una creación.
 */

/**
 * Un `draftId` nuevo. `crypto.randomUUID` ya lo usa este asistente para el
 * `clientOpId` (APK en `https://localhost`, web en https, jsdom), pero exige
 * contexto seguro: en uno que no lo sea (un `http://` de pruebas en la red
 * local) no existe, y una puerta que lanza aquí dejaría al operario sin
 * asistente. Basta con que no se repita en la pestaña.
 */
export const newCreationDraftId = (): string => {
  const cryptoApi = globalThis.crypto as Crypto | undefined;
  if (typeof cryptoApi?.randomUUID === 'function') return cryptoApi.randomUUID();
  return `draft-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
};

const pathnameOf = (url: string | undefined): string => {
  try {
    return new URL(url ?? '', 'http://localhost').pathname;
  } catch {
    return '/';
  }
};

/**
 * Si el borrador en curso es de OTRO padre que el de la URL, a la pantalla del
 * padre de la URL; si no, `null`.
 *
 * Auditoría de creación 2.3: ningún loader del asistente miraba la URL, así
 * que «atrás» hasta `/work-orders/X/new-action/details/…`, el «Editar» de un
 * resumen viejo o una URL compartida enseñaban — y guardaban — el borrador de
 * la OT Y bajo la URL de la X. Se va a la X (lo que dice la URL) sin tocar el
 * borrador, que sigue siendo de la Y.
 *
 * Padre de cada árbol: en el del recorrido, `:routeId` frente al `routeId` del
 * borrador (la básica cuelga de la Order del recorrido, que no es el
 * `:routeId`); en los de OT y campaña, `:id` frente a `orderParentId`.
 *
 * 🔴 Solo redirige si los DOS padres existen y difieren (Ruling 8): un borrador
 * sin padre (sesión vieja, semilla a mano de un E2E) o una ruta sin él siguen
 * como hoy.
 */
export const foreignCreationParentRedirect = (
  args: { params?: Params; request?: { url?: string } } | undefined,
  store: Pick<Storage, 'getItem'> = sessionStorage
): Response | null => {
  const params = args?.params ?? {};
  const draft = readCreationDraft(store);
  if (!draft) return null;

  const [draftParent, urlParent] = params.routeId
    ? [draft.routeId, params.routeId]
    : [draft.orderParentId, params.id];
  if (typeof draftParent !== 'string' || !draftParent || !urlParent) return null;
  if (draftParent === urlParent) return null;

  // La pantalla del padre es la URL hasta `/new-action`: `/work-orders/X`,
  // `/work-orders/campaign/X` o `/surveillance-route/R`.
  const pathname = pathnameOf(args?.request?.url);
  const cut = pathname.indexOf('/new-action');
  // REPLACE: la redirección de un loader es un PUSH en React Router 7, también
  // tras un «atrás»; la ficha se apilaba encima de esta URL y cada «atrás»
  // volvía a caer aquí y a rebotar (revisión de la Task 12a). Un rebote nunca
  // queda en el historial.
  return replace(cut > 0 ? pathname.slice(0, cut) : '/');
};

/**
 * Nombres de las fotos del borrador de creación: los de `editionImages` de la
 * copia de «Datos Act». Es la LISTA que el guardado sube.
 *
 * 🔴 No la carpeta `action` de OPFS (spec 2026-11-02 § 6, «Fotos y croquis
 * entran en el ámbito»): esa carpeta es de todo el origen, la comparten las
 * pestañas y la llena también «Datos Act» de VALIDACIÓN con las fotos de la
 * actuación que se valida. Subir «lo que haya» se llevaba fotos de otra
 * actuación a esta. Y tampoco `selected-actions-files`: nadie la escribe en
 * `src` (Ruling 6), así que como lista daría siempre vacía.
 *
 * Creación no tiene lista de cambios (`data-changes` es de validación): todas
 * las fotos de `editionImages` son nuevas. Sin «Datos Act» (nunca se abrió) no
 * hay fotos.
 */
export const creationDraftPhotoNames = (
  editionData: { editionImages?: unknown } | null | undefined
): string[] => {
  let images: unknown = editionData?.editionImages;
  if (typeof images === 'string') {
    try {
      images = JSON.parse(images);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(images)) return [];
  return images
    .map((image: { name?: unknown; id?: unknown } | null) => image?.name ?? image?.id)
    .filter((name): name is string => typeof name === 'string' && name.length > 0);
};

/**
 * Adónde lleva «Editar» del RESUMEN de creación (`…/new-action/summary/:id`).
 *
 * El asistente de creación no lleva id en la URL: edita el borrador que haya
 * en `new-action-data`. Si después de guardar A una puerta empezó B bajo el
 * MISMO padre (el cronómetro, en el recorrido, crea básicas seguidas) y se
 * vuelve con «atrás» al resumen de A, «Editar» abría el asistente de B — el
 * padre coincide, así que la comprobación de los loaders no lo ve — y el
 * operario cambiaba B creyendo cambiar A (auditoría de creación 2.3; spec
 * 2026-11-02 wizard-draft-ownership, invariantes I1/I3).
 *
 * - Borrador de ESTA actuación (lo normal, recién guardada): `null` → el
 *   asistente, como siempre.
 * - Borrador de otra actuación, o ninguno: la edición de ESTA por validación,
 *   que va por el `:id` de la URL, en el mismo árbol (recorrido, campaña o el
 *   global de Órdenes) y en su primera pestaña editable (la básica no tiene
 *   Operarios: Ubicación).
 */
export const creationSummaryEditPath = ({
  pathname,
  actionId,
  isBasicAction,
  draft,
}: {
  pathname: string;
  actionId: string | undefined;
  isBasicAction: boolean;
  draft: Record<string, unknown> | null;
}): string | null => {
  if (!actionId || draft?.actionId === actionId) return null;

  const routeId = pathname.match(/^\/surveillance-route\/([^/]+)\/new-action(?:\/|$)/)?.[1];
  const campaignId = pathname.match(/^\/work-orders\/campaign\/([^/]+)\/new-action(?:\/|$)/)?.[1];
  const validationBase = routeId
    ? `/surveillance-route/${routeId}/actions-validation`
    : campaignId
      ? `/work-orders/campaign/${campaignId}/actions-validation`
      : '/work-orders/actions-validation';
  const firstTab = isBasicAction ? 'location' : 'operators-and-vehicles';
  return `${validationBase}/${actionId}/${firstTab}`;
};
